/**
 * WHAT A PASSENGER CAN BE HELD UP BY — the delay catalog, and the rule that retires
 * reasons as a trip progresses.
 *
 * ── ⚠️ THE LIST IS THE CLIENT'S, NOT THE SERVER'S ─────────────────────────────────
 * There is no options endpoint. Both mobile apps carry this same array, and the backend
 * validates nothing about it beyond storing what it is sent. So the order below is not
 * cosmetic — it is data, and the locking rule reads it as an ordinal. Reordering these
 * rows changes which reasons lock, and inserting one in the middle re-numbers every
 * reason after it. Append; do not insert.
 *
 * ── ⚠️ AND IT MUST MATCH THE DRIVER'S COPY ────────────────────────────────────────
 * The driver app renders a delay from `reason_code` against its own copy of this list. A
 * code only this portal knows renders on their side as an unknown row.
 */

/**
 * The reasons, in order. `id` goes on the wire as `reason_code`; `label` goes as
 * `reason_text` — EXCEPT for `other_delay`, where the passenger's own words are sent
 * instead and this label is only what the row is called in the picker.
 */
export const DELAY_REASONS = [
    {id: 'stuck_at_immigration', label: 'Stuck at Immigration'},
    {id: 'waiting_for_baggage', label: 'Waiting for Baggage'},
    {id: 'lost_baggage', label: 'Lost Baggage'},
    {id: 'held_at_customs', label: 'Held at Customs'},
    {id: 'other_delay', label: 'Other Delay'},
]

/** The one row that asks for free text instead of sending its own label. */
export const OTHER_DELAY_ID = 'other_delay'

/**
 * How much longer, as offered.
 *
 * ⚠️ FOUR VALUES AND NO CUSTOM ENTRY, deliberately. A passenger stuck at immigration does
 * not know how long they will be, and a minutes field invites a precision none of them
 * has. The chips are a rough signal to a driver deciding whether to circle the terminal.
 */
export const DELAY_MINUTE_OPTIONS = [
    {label: '15 min', minutes: 15},
    {label: '30 min', minutes: 30},
    {label: '45 min', minutes: 45},
    {label: '1 hr+', minutes: 60},
]

/**
 * WHICH REASONS ARE STILL AVAILABLE, given everything already reported on this trip.
 *
 * ⚠️ A WALK THROUGH AN AIRPORT ONLY GOES ONE WAY. Immigration comes before baggage, which
 * comes before customs — so once a passenger has reported being held at customs, "stuck at
 * immigration" is not a thing that can happen to them next. The rule is exactly that: take
 * the highest catalog index ever reported, and lock everything BELOW it.
 *
 * ⚠️ THE HIGHEST REASON ITSELF STAYS OPEN, because being held at customs twice is ordinary.
 * `other_delay` is last in the list, so it is never locked by anything.
 *
 * ⚠️ AN UNKNOWN CODE LOCKS NOTHING. `indexOf` returns -1, which is below every real index
 * and so cannot raise the bar. A reason this build has not heard of must not silently
 * retire the ones it has.
 *
 * @param {Array<{reason_code?: string}>|null} delays every delay reported on the trip
 * @returns {Set<string>} the ids that must be disabled
 */
export function lockedDelayReasonIds(delays) {
    let highest = -1
    for (const delay of delays || []) {
        const index = DELAY_REASONS.findIndex((r) => r.id === delay?.reason_code)
        if (index > highest) highest = index
    }
    if (highest < 0) return new Set()
    return new Set(DELAY_REASONS.slice(0, highest).map((r) => r.id))
}

/** The catalog row for a code, or null. Used to render history the picker never saw. */
export function delayReasonById(id) {
    return DELAY_REASONS.find((r) => r.id === id) || null
}
